import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Truck, CheckCircle2, ArrowLeft, Route, MapPin } from "lucide-react";
import { toast } from "sonner";

const LANES = [
  { key: "pickup", label: "Ready for pickup", icon: Package, match: (j) => j.status === "assigned" },
  { key: "onboard", label: "On board", icon: Truck, match: (j) => ["picked_up", "in_transit"].includes(j.status) },
  { key: "done", label: "Delivered", icon: CheckCircle2, match: (j) => ["delivered", "onforwarded"].includes(j.status) },
];

export default function DriverBoard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => api.get("/driver/jobs").then((r) => setJobs(r.data.filter((j) => !j.transferred_away))).catch(() => setJobs([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible" && navigator.onLine) load(); };
    const t = setInterval(tick, 45000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", tick); };
  }, [load]);

  const onDrop = async (laneKey) => {
    const job = jobs?.find((j) => j.id === dragId);
    setDragId(null);
    if (!job) return;
    if (laneKey === "onboard" && job.status === "assigned") {
      try {
        await api.post(`/driver/jobs/${job.id}/status`, { status: "picked_up" });
        toast.success(`${job.tracking_number} picked up — on board, customer notified`);
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    } else if (laneKey === "done") {
      toast.info("Deliveries need a signature — complete the POD from your Jobs page");
    }
  };

  const sortSeq = (a, b) => (a.run_sequence ?? 999) - (b.run_sequence ?? 999);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Run board</h1>
            <p className="mt-1 text-sm text-muted-foreground">Drag a job onto "On board" once it's in the van.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/driver")} data-testid="board-back-jobs" className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              <ArrowLeft className="h-4 w-4" /> Jobs
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/driver/route")} data-testid="board-route-map" className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              <Route className="h-4 w-4" /> Run map
            </Button>
          </div>
        </div>

        {jobs === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="driver-board">
            {LANES.map((lane) => {
              const laneJobs = jobs.filter(lane.match).sort(sortSeq);
              return (
                <section key={lane.key} data-testid={`driver-lane-${lane.key}`}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(lane.key)}
                  className="min-h-[50vh] rounded-lg border bg-secondary/30 p-3">
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-kilted-green">
                    <lane.icon className="h-4 w-4" /> {lane.label}
                    <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs">{laneJobs.length}</span>
                  </h2>
                  <div className="space-y-2">
                    {laneJobs.map((j) => (
                      <div key={j.id} draggable={j.status === "assigned"} onDragStart={() => setDragId(j.id)}
                        onClick={() => navigate(`/orders/${j.id}`)} role="button" tabIndex={0}
                        data-testid={`board-card-${j.tracking_number}`}
                        className={`rounded-md border bg-white p-3 text-sm shadow-sm transition-colors hover:border-kilted-gold ${j.status === "assigned" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-heading font-bold">{j.tracking_number}</span>
                          {j.run_sequence != null && (
                            <span className="rounded-full bg-kilted-gold/20 px-2 py-0.5 text-[10px] font-bold text-kilted-green">Stop {j.run_sequence + 1}</span>
                          )}
                        </div>
                        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" /> {j.pickup?.city} → {j.dropoff?.city}
                        </p>
                        <p className="text-xs text-muted-foreground">{j.size} · {j.weight_kg}kg</p>
                      </div>
                    ))}
                    {laneJobs.length === 0 && <p className="py-6 text-center text-xs text-muted-foreground">Nothing here</p>}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
